package co.maytes.standupbot.dashboard.components.widgets

import androidx.compose.runtime.Composable
import co.maytes.standupbot.dashboard.components.motion.CountUp
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.classNames
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText

/**
 * Metric tile for dashboards. Large mono value, eyebrow label above, optional
 * trend badge to the right of the value, optional muted subtitle below. The
 * mono value at 42px is the dashboard's signature precision moment — use
 * sparingly (3-4 per page max).
 */
@Composable
fun StatCard(
    label: String,
    value: String,
    trend: String? = null,
    trendPositive: Boolean? = null,
    subtitle: String? = null,
    icon: (@Composable () -> Unit)? = null,
    modifier: Modifier = Modifier,
) {
    Card(
        modifier = Modifier
            .fillMaxWidth()
            .styleModifier {
                property("height", "100%")
                property("min-height", "140px")
                property("justify-content", "space-between")
            }
            .then(modifier),
    ) {
        StatHeader(label = label, icon = icon)
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .margin(top = Spacing.sm),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.SpaceBetween,
        ) {
            SpanText(
                text = value,
                modifier = Modifier
                    .classNames("sb-stat-value")
                    .color(Palette.textPrimary),
            )
            if (trend != null) {
                val tone = when (trendPositive) {
                    true -> BadgeTone.Success
                    false -> BadgeTone.Error
                    null -> BadgeTone.Neutral
                }
                Badge(label = trend, tone = tone)
            }
        }
        // Always render the subtitle slot so cards align vertically. If the
        // caller didn't pass one, use a non-breaking space to reserve the line.
        SpanText(
            text = subtitle ?: " ",
            modifier = Modifier
                .margin(top = Spacing.xs)
                .fontSize(Typography.bodySmall.size)
                .color(Palette.textTertiary),
        )
    }
}

@Composable
private fun StatHeader(label: String, icon: (@Composable () -> Unit)?) {
    if (icon == null) {
        Eyebrow(text = label)
        return
    }
    Row(
        verticalAlignment = Alignment.CenterVertically,
        modifier = Modifier.gap(Spacing.xs),
    ) {
        Box(
            modifier = Modifier.styleModifier {
                property("color", Palette.textTertiary.cssRgb())
                property("display", "inline-flex")
                property("flex-shrink", "0")
            },
        ) { icon() }
        Eyebrow(text = label)
    }
}

@Composable
fun IntStatCard(
    label: String,
    value: Int,
    trend: String? = null,
    trendPositive: Boolean? = null,
    subtitle: String? = null,
    icon: (@Composable () -> Unit)? = null,
    modifier: Modifier = Modifier,
    format: (Int) -> String = { it.toString() },
) {
    Card(
        modifier = Modifier
            .fillMaxWidth()
            .styleModifier {
                property("height", "100%")
                property("min-height", "140px")
                property("justify-content", "space-between")
            }
            .then(modifier),
    ) {
        StatHeader(label = label, icon = icon)
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .margin(top = Spacing.sm),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.SpaceBetween,
        ) {
            CountUp(
                value = value,
                format = format,
                modifier = Modifier
                    .classNames("sb-stat-value")
                    .color(Palette.textPrimary),
            )
            if (trend != null) {
                val tone = when (trendPositive) {
                    true -> BadgeTone.Success
                    false -> BadgeTone.Error
                    null -> BadgeTone.Neutral
                }
                Badge(label = trend, tone = tone)
            }
        }
        SpanText(
            text = subtitle ?: " ",
            modifier = Modifier
                .margin(top = Spacing.xs)
                .fontSize(Typography.bodySmall.size)
                .color(Palette.textTertiary),
        )
    }
}
